import { Link } from 'react-router-dom';
/* La lista dei backtest: scheda sotto i 720px, tabella sopra.
 *
 * Perché non `ListaRisultati` di `componenti/`: quella riga porta profitto,
 * drawdown, operazioni, pari rischio e periodo, ma NON Tenuta e Nei ribassi.
 * Su questa schermata i due indicatori sono il motivo per cui si guarda la
 * lista — il caso ETH della libreria (+62.877% totale, tenuta 24%) esiste
 * proprio per dire che il profitto senza la tenuta accanto è una trappola — e
 * ognuno dei due deve portare colore e verdetto, sempre visibili.
 * La richiesta di estendere il componente condiviso è in RICHIESTE-CONDIVISE.md;
 * finché non viene accolta la variante vive qui.
 *
 * Il qualificatore di affidabilità è l'unico pezzo che qui NON si mostra, e per
 * richiesta esplicita: su cinquanta righe scorse per scegliere quale aprire,
 * cinquanta volte «diviso per cicli di halving» è ingombro, non informazione.
 * Vive intero nel dettaglio, dove il numero si valuta; il segnale di fragilità
 * resta anche qui, attenuato ma presente.
 *
 * Entrambe le forme sono renderizzate sempre e sceglie il CSS: il primo
 * fotogramma è già giusto, ruotare il telefono non rimonta l'albero e la
 * stampa funziona. Le intestazioni di colonna NON sono cliccabili: ordinare è
 * una query e vive nel suo controllo dedicato.
 *
 * LA CASELLA DEL CONFRONTO. Ogni riga porta una casella che la mette nel
 * confronto (`/backtest/confronto`). Due vincoli, entrambi imparati qui:
 *
 *  - la casella NON deve navigare. Nella tabella il clic sulla riga apre il
 *    dettaglio, quindi quello sulla casella viene fermato con
 *    `stopPropagation` prima di arrivarci; nella scheda tutto è dentro un
 *    `Link` — che esiste perché il tasto centrale apra il dettaglio in una
 *    scheda nuova — e la casella sta perciò FUORI dal collegamento, non dentro
 *    con un `preventDefault`: un controllo interattivo annidato in un `<a>` è
 *    marcatura non valida, e ci si accorge del guasto tardi;
 *  - lo stato dei selezionati NON vive qui. Arriva dall'URL (`statoConfronto.ts`)
 *    e ci torna: una selezione costata la fatica di trovare due righe fra un
 *    milione e mezzo si condivide e sopravvive al ricaricamento.
 */

import type { RendimentoAnnuale } from '../../api/tipi';
import { AnniControMercato } from '../../componenti/AnniControMercato';
import {
  ContenitoreDati,
  Distintivo,
  DistintivoBotInFunzione,
  IconaStrategia,
  NumeroConSoglia,
  SegnaleBotInFunzione,
  TernaMetriche,
  VeloInterrogazione,
  divisioneDaMetodo,
  type BotCollegato,
} from '../../componenti';
import { drawdown as fmtDrawdown, num, pct, timeframe } from '../../lib/formato';
import {
  leggiAPariRischio,
  leggiNeiRibassi,
  leggiTenuta,
  tonoRendimento,
  type LetturaSoglia,
} from '../../lib/metriche';
import type { StatoDati } from '../../api/usaRichiesta';
import './backtest.css';

export interface RigaBacktest {
  id: number;
  strategia: string;
  asset: string;
  currency: string;
  candleSize: number;
  gruppo: number | null;
  profitto: number | null;
  drawdown: number | null;
  operazioni: number | null;
  periodo: string;
  tenuta: number | null;
  tenutaMetodo: 'ciclo' | 'meta' | null;
  ribassi: number | null;
  anniNegativi: number | null;
  calmar: number | null;
  preferito: boolean;
  /** I tradebot che girano ESATTAMENTE questa configurazione, riconosciuti dai
      parametri e non da un id di provenienza (`lib/chiaveConfig.ts`).
      `[]` significa «cercato, nessuno»; `null` significa «l'elenco dei bot non e'
      ancora arrivato», che non e' la stessa cosa e non va scritto come tale. */
  bot?: BotCollegato[] | null;
  /** I rendimenti anno per anno col mercato accanto: e' l'informazione che nessun totale
      riassume, e si cerca mentre si scorre, non dopo essere entrati in una riga. */
  anni: RendimentoAnnuale[] | null;
}

export interface PropsListaBacktest {
  righe: RigaBacktest[];
  /** Gli id gia' nel confronto: la casella spuntata viene da qui, non da uno
   *  stato locale, perche' la selezione vive nell'URL e non nella lista. */
  selezionati: number[];
  /** Un tocco sulla casella. Chi la riceve decide se accoglierla o rifiutarla
   *  (il tetto e' quattro) e in quel caso lo DICE: un clic ignorato in silenzio
   *  si legge come un'interfaccia rotta. */
  onSeleziona: (id: number) => void;
  stato: StatoDati;
  /** Quale vincolo ha svuotato la lista: senza, il vuoto è un enigma. */
  causaVuoto: string;
  azioneVuoto?: { etichetta: string; onClick: () => void };
  messaggioErrore?: string;
  onRiprova?: () => void;
  onApri: (id: number) => void;
  /** Testo del velo mentre il server rilancia la query. */
  interrogazioneInCorso?: string | null;
}

function coppia(r: RigaBacktest): string {
  return `${r.asset}/${r.currency} · ${timeframe(r.candleSize)}`;
}

function letturaTenuta(r: RigaBacktest): LetturaSoglia {
  return leggiTenuta(r.tenuta, divisioneDaMetodo(r.tenutaMetodo));
}

function letturaRibassi(r: RigaBacktest): LetturaSoglia {
  return leggiNeiRibassi(r.ribassi, r.anniNegativi);
}

export function ListaBacktest({
  righe,
  selezionati,
  onSeleziona,
  stato,
  causaVuoto,
  azioneVuoto,
  messaggioErrore,
  onRiprova,
  onApri,
  interrogazioneInCorso,
}: PropsListaBacktest) {
  return (
    <div className="sb-relativo">
      {interrogazioneInCorso && <VeloInterrogazione testo={interrogazioneInCorso} />}

      <ContenitoreDati
        stato={stato}
        causaVuoto={causaVuoto}
        azioneVuoto={azioneVuoto}
        messaggioErrore={messaggioErrore}
        onRiprova={onRiprova}
      >
        {/* --- telefono: schede --- */}
        <div className="sb-lista">
          {righe.map((r) => (
            <SchedaBacktest
              key={r.id}
              riga={r}
              scelto={selezionati.includes(r.id)}
              onSeleziona={onSeleziona}
            />
          ))}
        </div>

        {/* --- tablet e desktop: tabella --- */}
        <div className="sb-tabella-guscio">
          <div className="sb-scorri-x">
            <table className="sb-tabella" style={{ minWidth: 840 }}>
              <thead>
                <tr>
                  {/* La colonna della selezione sta per PRIMA e ha un nome scritto:
                      una casella senza etichetta, in cima a una tabella di numeri, non
                      dice cosa fa finche' non la si prova. */}
                  <th style={{ width: 76 }}>Confronta</th>
                  <th>Strategia</th>
                  {/* Se questa configurazione e' GIA' un bot. Colonna sua e non
                      un distintivo in piu' nella cella della strategia: la' andrebbe
                      a capo e allungherebbe una riga che si scorre. */}
                  <th>Bot</th>
                  <th className="sb-col-profitto">Profitto</th>
                  <th className="sb-col-drawdown">Drawdown</th>
                  <th>Ops</th>
                  <th>Tenuta</th>
                  {/* «A pari rischio» sta accanto a Tenuta perché è la coppia con cui si
                      scarta una riga: il profitto normalizzato al drawdown obiettivo dice
                      quanto vale davvero, la tenuta se vale ancora. Ha perso
                      `sb-solo-desktop` insieme al posto: fra due colonne sempre presenti,
                      una che scompare a 1099px lascerebbe la coppia dimezzata proprio sul
                      tablet, dove la lista si scorre. La tabella scorre in orizzontale,
                      quindi una colonna corta in più non taglia niente. */}
                  <th>Pari rischio</th>
                  <th>Nei ribassi</th>
                  {/* Solo da 1100px: la scelta è del CSS, non di un ramo JS. */}
                  <th className="sb-solo-desktop" style={{ minWidth: 150 }}>
                    Anno per anno
                  </th>
                  <th className="sb-solo-desktop">Calmar</th>
                  <th>Periodo</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r) => (
                  <RigaTabella
                    key={r.id}
                    riga={r}
                    onApri={onApri}
                    scelto={selezionati.includes(r.id)}
                    onSeleziona={onSeleziona}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </ContenitoreDati>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function RigaTabella({
  riga: r,
  onApri,
  scelto,
  onSeleziona,
}: {
  riga: RigaBacktest;
  onApri: (id: number) => void;
  scelto: boolean;
  onSeleziona: (id: number) => void;
}) {
  const t = letturaTenuta(r);
  const b = letturaRibassi(r);

  return (
    <tr
      onClick={() => onApri(r.id)}
      style={{ cursor: 'pointer' }}
      className={scelto ? 'sb-bt-riga--scelta' : undefined}
    >
      {/* Il clic sulla riga apre il dettaglio, quindi quello sulla casella non
          deve arrivarci: senza `stopPropagation` spuntare una riga la
          aprirebbe, cioe' proprio il contrario di quello che si voleva. */}
      <td onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          className="sb-bt-spunta"
          checked={scelto}
          onChange={() => onSeleziona(r.id)}
          aria-label={`Metti ${r.strategia} #${r.id} nel confronto`}
        />
      </td>

      <td className="sb-cella-testo">
        <span className="sb-tabella__strategia">
          <IconaStrategia nome={r.strategia} dim={24} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sb-s-3)', flexWrap: 'wrap' }}>
              {/* Un `tr` non si puo' avvolgere in un collegamento, quindi il collegamento sta
                  qui, sul nome: e' il punto dove si guarda per primo, e da qui il tasto
                  centrale apre il dettaglio in una scheda nuova senza perdere la lista.
                  Il clic sulla riga continua a funzionare come prima. */}
              <Link
                to={`/backtest/${r.id}`}
                onClick={(e) => e.stopPropagation()}
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {r.strategia}
              </Link>
              {r.gruppo != null && <Distintivo contorno>gruppo #{r.gruppo}</Distintivo>}
              {r.preferito && <Distintivo tono="accento">preferito</Distintivo>}
            </span>
            <span className="sb-tabella__sotto">{coppia(r)}</span>
          </span>
        </span>
      </td>

      <td>
        <SegnaleBotInFunzione voci={r.bot} />
      </td>

      <td className={`sb-tono-${tonoRendimento(r.profitto)}`}>
        {r.profitto == null ? 'n/d' : pct(r.profitto)}
      </td>
      <td className="sb-tono-perdita">
        {r.drawdown == null ? 'n/d' : fmtDrawdown(r.drawdown)}
      </td>
      <td className="sb-tono-neutro" style={{ fontWeight: 'var(--sb-peso-medio)' }}>
        {r.operazioni == null ? 'n/d' : num(r.operazioni)}
      </td>

      {/* Densità scorrevole: valore, verdetto e colore restano, il qualificatore no.
          Su cinquanta righe «diviso per cicli di halving» sono cinquanta righe di testo
          che non servono a scegliere quale riga aprire. Sta intero nel dettaglio, che è
          dove il numero si giudica. */}
      <td>
        <NumeroConSoglia densita="cella-scorrevole" lettura={t} />
      </td>
      <td className="sb-tono-neutro">{leggiAPariRischio(r.profitto, r.drawdown).valore}</td>
      <td>
        <NumeroConSoglia densita="cella-scorrevole" lettura={b} />
      </td>

      {/* Anno per anno da 1100px in su: sotto, la stessa cosa sta nella scheda. */}
      <td className="sb-solo-desktop" style={{ minWidth: 168 }}>
        <AnniControMercato anni={r.anni} />
      </td>
      <td className="sb-tono-neutro sb-solo-desktop" style={{ fontWeight: 'var(--sb-peso-medio)' }}>
        {r.calmar == null ? 'n/d' : num(r.calmar, 2)}
      </td>

      <td
        className="sb-tono-neutro"
        style={{
          fontWeight: 'var(--sb-peso-medio)',
          fontSize: 'var(--sb-t-105)',
          color: 'var(--sb-testo-tenue)',
        }}
      >
        {r.periodo}
      </td>
    </tr>
  );
}

/* -------------------------------------------------------------------------- */

function SchedaBacktest({
  riga: r,
  scelto,
  onSeleziona,
}: {
  riga: RigaBacktest;
  scelto: boolean;
  onSeleziona: (id: number) => void;
}) {
  const t = letturaTenuta(r);
  const b = letturaRibassi(r);

  return (
    <div className={`sb-scheda-risultato${scelto ? ' sb-scheda-risultato--scelta' : ''}`}>
      {/* Un vero collegamento e non un pulsante che naviga: il tasto centrale, il
          Ctrl-clic e «apri in una nuova scheda» funzionano solo se c'e' un indirizzo
          da aprire. Con un `onClick` il browser non ha niente da mettere nella scheda
          nuova, e chi sta scorrendo una lista di centinaia di righe perde il posto ogni
          volta che vuole guardare un dettaglio. */}
      <Link
        to={`/backtest/${r.id}`}
        style={{ all: 'unset', display: 'block', width: '100%', cursor: 'pointer' }}
      >
        <div className="sb-scheda-risultato__testata">
          <IconaStrategia nome={r.strategia} dim={22} />
          <span className="sb-scheda-risultato__nome">{r.strategia}</span>
          {/* Sotto i 720px la colonna non c'e': il distintivo sta in testata,
              dove si legge prima dei numeri. */}
          {r.bot != null && r.bot.length > 0 && <DistintivoBotInFunzione voci={r.bot} />}
          {r.preferito && <Distintivo tono="accento">preferito</Distintivo>}
        </div>

        <div className="sb-nota" style={{ marginBottom: 'var(--sb-s-5)' }}>
          {coppia(r)}
          {r.gruppo != null && ` · gruppo #${r.gruppo}`}
        </div>

        {/* I tre numeri restano tre anche a 390px: la regola non cambia con la
            larghezza dello schermo. Il componente condiviso invece di tre
            riquadri scritti a mano, che si erano già scostati da lui. */}
        <TernaMetriche
          densita="riquadri"
          profitto={r.profitto}
          drawdown={r.drawdown}
          operazioni={r.operazioni}
          periodo={r.periodo}
        />

        {/* Quattro numeri su una riga, senza i verdetti scritti sotto.
            Nella lista si scorre: il verdetto per esteso («tiene, indebolita») allungava la
            scheda di due righe per dire quello che il colore gia' dice, e la scheda finiva per
            occupare uno schermo intero su telefono. Il testo integrale resta nel dettaglio,
            dove si valuta invece di scorrere. */}
        <div className="sb-bt-numeri">
          <NumeroSecco etichetta="TENUTA" valore={t.valore} tono={t.tono} />
          <NumeroSecco etichetta="RIBASSI" valore={b.valore} tono={b.tono} />
          <NumeroSecco etichetta="PARI RISCHIO" valore={leggiAPariRischio(r.profitto, r.drawdown).valore} />
          <NumeroSecco etichetta="CALMAR" valore={r.calmar == null ? 'n/d' : num(r.calmar, 2)} />
        </div>

        {/* Anno per anno contro il mercato: su telefono e' qui che si legge, e vale la
            quarantina di pixel che occupa. Lo sfondo di ogni anno dice se la strategia ha
            battuto il mercato -- non se il mercato e' sceso, che e' un'altra domanda. */}
        {r.anni && r.anni.length > 0 && (
          <div className="sb-anni-blocco">
            <span className="sb-etichetta">Anno per anno · contro il mercato</span>
            <AnniControMercato anni={r.anni} />
          </div>
        )}

      </Link>

      {/* FUORI dal collegamento, non dentro: un controllo interattivo annidato in
          un `<a>` e' marcatura non valida e costringerebbe a spegnere la
          navigazione con `preventDefault` a ogni tocco. Qui il collegamento
          finisce sopra e la casella e' una riga a se', alta quanto un tocco
          comodo e con scritto cosa fa. */}
      <label className="sb-bt-scelta">
        <input
          type="checkbox"
          className="sb-bt-spunta"
          checked={scelto}
          onChange={() => onSeleziona(r.id)}
        />
        <span>{scelto ? 'Nel confronto' : 'Metti nel confronto'}</span>
      </label>
    </div>
  );
}

/**
 * Il riquadro di Tenuta e Nei ribassi nella scheda del telefono.
 *
 * Senza il qualificatore, per la stessa ragione della tabella: la scheda è la
 * forma della lista sotto i 720px, e lì si scorre. Il bordo tratteggiato del
 * numero fragile resta — è il segnale che non si può togliere senza far
 * sembrare solido un dato che non lo è — e il testo che spiega perché sta nel
 * dettaglio, dove c'è lo spazio per leggerlo.
 */
/** Un numero con la sua etichetta, senza verdetto: la forma per una lista che si scorre. */
function NumeroSecco({
  etichetta,
  valore,
  tono,
}: {
  etichetta: string;
  valore: string;
  tono?: LetturaSoglia['tono'];
}) {
  return (
    <div className="sb-bt-numero">
      <span className="sb-bt-numero__nome">{etichetta}</span>
      <span className={`sb-bt-numero__valore${tono ? ` sb-tono-${tono}` : ''}`}>{valore}</span>
    </div>
  );
}

